import { supabaseClient } from "../supabase";
import { AddressSuggestionEF, FeatureMember, GeocoderResponseMetaDataType } from "./types";
import {
  convertAddressInputCreateForSupa,
  convertAddressSuggestion,
  convertAddressUpdateForSupa,
  convertSupaAddress,
} from "./converters";
import {BaseAddressClient, AddressSuggestion, Address, AddressInput, User } from "@core/types";

type AddressSuggestionEFType = {
  addresses: AddressSuggestionEF[];
  raw: {
    featureMember: FeatureMember[];
    GeocoderResponseMetaData: GeocoderResponseMetaDataType;
  };
};

class AddressClient extends BaseAddressClient {
  async getAddressSuggestion(address: string): Promise<AddressSuggestion[] | null> {
    if (!address) return null;

    const { data, error } = await supabaseClient.functions.invoke<AddressSuggestionEFType>("address-suggestion", {
      body: {
        query: address,
      },
    });

    if (error) {
      throw error;
    }

    return !!data ? data.addresses.map(convertAddressSuggestion) : null;
  }

  async getCustomerAddresses(userId: User["id"]): Promise<Address[] | null> {
    if (!userId) return null;

    const { data } = await supabaseClient
      .from("customer_addresses")
      .select("*")
      .eq("customer_id", parseInt(userId))
      .throwOnError();

    const addresses = data?.map(convertSupaAddress);

    return !!addresses?.length ? addresses : null;
  }

  async createCustomerAddress(customerAddressInput: AddressInput, userId: User["id"]): Promise<Address | null> {
    if (!userId) return null;
    const customerSupa = convertAddressInputCreateForSupa(customerAddressInput, userId);
    const { data: newAddress } = await supabaseClient
      .from("customer_addresses")
      .insert(customerSupa)
      .select("*")
      .single()
      .throwOnError();

    return !!newAddress ? convertSupaAddress(newAddress) : null;
  }

  async deleteCustomerAddress(addressId: Address["id"]): Promise<Address["id"] | null> {
    const { data } = await supabaseClient
      .from("customer_addresses")
      .delete()
      .eq("id", parseInt(addressId!))
      .select()
      .single()
      .throwOnError();

    const deletedAddressId = data?.["id"];

    return !!deletedAddressId ? String(deletedAddressId) : null;
  }

  async updateCustomerAddress(customerAddress: Address, userId: User["id"] | undefined): Promise<Address | null> {
    if (!userId) throw new Error("no customer id");
    const customerSupa = convertAddressUpdateForSupa(customerAddress, userId);

    const { data: updatedAddress } = await supabaseClient
      .from("customer_addresses")
      .update(customerSupa)
      .eq("id", parseInt(customerAddress.id!))
      .select()
      .single()
      .throwOnError();

    return !!updatedAddress ? convertSupaAddress(updatedAddress) : null;
  }
}

export default AddressClient;
